/**
 * First-login MFA setup service.
 *
 * These two calls are pre-authentication: they're gated by the temporary
 * session_id returned from /auth/login when a user has no MFA device yet
 * (requires_mfa_setup=true), not by a JWT.
 */
import api, { getErrorMessage } from './api'
import {
  prepareRegistrationOptions,
  serializeRegistrationCredential,
  webauthnErrorMessage,
} from './webauthn'

export interface MfaSetupBegin {
  secret: string
  qr_uri: string
}

export interface MfaSetupComplete {
  access_token: string
  token_type: string
  expires_in: number
  backup_codes: string[]
}

export interface MfaSetupVerifyParams {
  session_id: string
  code: string
  device_label?: string
  remember_device?: boolean
  device_name?: string
}

export async function mfaSetupBegin(sessionId: string): Promise<MfaSetupBegin> {
  const resp = await api.post<MfaSetupBegin>('/v1/auth/mfa/setup/begin', {
    session_id: sessionId,
  })
  return resp.data
}

export async function mfaSetupVerify(params: MfaSetupVerifyParams): Promise<MfaSetupComplete> {
  const resp = await api.post<MfaSetupComplete>('/v1/auth/mfa/setup/verify', params)
  return resp.data
}

// ---------------------------------------------------------------------------
// First-login PASSKEY setup (alternative to the TOTP path above)
// ---------------------------------------------------------------------------

interface MfaSetupPasskeyBeginResponse {
  options: Record<string, unknown>
  challenge_token: string
}

/**
 * Full first-login passkey setup: begin → browser WebAuthn prompt → complete.
 *
 * Gated by the temp setup session_id from /auth/login (requires_mfa_setup=true),
 * not by a JWT. On success the backend stores the passkey, issues backup codes,
 * activates the session, and returns the auth token + backup codes — mirroring
 * the TOTP path so the wizard converges on the same backup-codes screen.
 */
export async function mfaSetupPasskey(
  sessionId: string,
  label = 'Passkey',
): Promise<MfaSetupComplete> {
  // Step 1: registration options (gated by the setup session)
  let begin: MfaSetupPasskeyBeginResponse
  try {
    const resp = await api.post<MfaSetupPasskeyBeginResponse>(
      '/v1/auth/mfa/setup/passkey/begin',
      { session_id: sessionId },
    )
    begin = resp.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }

  // Step 2: browser WebAuthn prompt
  const publicKeyOptions = prepareRegistrationOptions(begin.options)
  let credential: PublicKeyCredential
  try {
    credential = (await navigator.credentials.create({
      publicKey: publicKeyOptions,
    })) as PublicKeyCredential
  } catch (err) {
    throw new Error(webauthnErrorMessage(err, 'Passkey setup was cancelled.'))
  }
  if (!credential) {
    throw new Error('No credential returned by the browser.')
  }

  // Step 3: verify + store on the server → returns auth token + backup codes
  try {
    const resp = await api.post<MfaSetupComplete>('/v1/auth/mfa/setup/passkey/complete', {
      session_id: sessionId,
      challenge_token: begin.challenge_token,
      credential: serializeRegistrationCredential(credential),
      passkey_label: label,
    })
    return resp.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}
